import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, User, Smartphone, Monitor, Building2, Lock, CheckCircle2 } from "lucide-react";
import { TestApplyDialog } from "@/features/tests/TestApplyDialog";
import { useAppStore } from "@/lib/store";
import type { TestProject } from "@/features/tests/TestCard";
import { getAppIcon } from "@/features/tests/appIcons";
import { isTestCompleted } from "@/lib/completedTests";
import { toast } from "sonner";

interface TestPreviewModalProps {
  project: TestProject | null;
  onClose: () => void;
  onStartTest: () => void;
}

export function TestPreviewModal({ project, onClose, onStartTest }: TestPreviewModalProps) {
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const user = useAppStore((s) => s.user);
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const balance = Number((user as any)?.balance ?? 0);
  const maintenancePaid = Number(
    (user as any)?.maintenancePaid ?? (user as any)?.maintenance_paid ?? 0,
  );
  const lockThreshold = Number(
    (user as any)?.lockThreshold ?? (user as any)?.lock_threshold ?? 800,
  );
  const isLocked = balance >= lockThreshold && maintenancePaid === 0 && user?.isAdmin !== 1;
  const lockThresholdUsd = lockThreshold / 100;
  const [completed, setCompleted] = useState(false);
  const [checkingCompletion, setCheckingCompletion] = useState(false);

  useEffect(() => {
    if (!project) return;
    let cancelled = false;
    setCheckingCompletion(true);
    isTestCompleted(project.id).then((done) => {
      if (!cancelled) {
        setCompleted(done);
        setCheckingCompletion(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [project]);

  if (!project) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleStartTestClick = () => {
    if (!isAuthenticated) {
      window.location.href = "/signup?redirect=/tests";
      return;
    }
    if (completed) {
      toast.error("You have already completed this test.");
      return;
    }
    if (isLocked) {
      setShowMaintenanceDialog(true);
      return;
    }
    onStartTest();
  };

  const BrandIcon = getAppIcon(project.appName);

  const renderBrandIcon = () => {
    if (!BrandIcon) return null;
    if (typeof BrandIcon === "string") {
      return (
        <img
          src={BrandIcon}
          alt={project.appName}
          className="h-5 w-5 object-contain shrink-0 rounded-sm"
          loading="lazy"
        />
      );
    }
    return <BrandIcon className="h-5 w-5 shrink-0" />;
  };

  return (
    <>
      <Dialog open={!!project} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="w-full max-w-lg sm:max-w-xl lg:max-w-2xl max-h-[85vh] overflow-y-auto p-3 sm:p-4">
          <DialogHeader className="pr-12 pb-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Badge variant="secondary">{project.category}</Badge>
              <Badge variant="outline" className="capitalize text-[11px]">
                {project.subcategory}
              </Badge>
              {isLocked && (
                <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                  <Lock className="h-3 w-3" />
                  Locked
                </span>
              )}
            </div>
            <DialogTitle className="text-lg leading-snug flex items-center gap-2">
              {renderBrandIcon()}
              {project.title}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2 text-xs text-muted-foreground">
              {project.platform === "mobile" ? (
                <Smartphone className="h-4 w-4" />
              ) : (
                <Monitor className="h-4 w-4" />
              )}
              {project.platform === "mobile"
                ? `Test ${project.appName} App`
                : `Test ${project.appName} Web App`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {isLocked && (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-sm">
                <p className="font-semibold text-amber-800 text-sm">Support the Platform</p>
                <p className="text-xs text-amber-700 mt-1">
                  To keep PataTasks running and improving, we ask for a small one-time $2 support
                  fee. This helps cover server costs and development.
                  <br />
                  No subscriptions, no hidden charges — just a one-time contribution to help us keep
                  things going.
                </p>
              </div>
            )}
            <div className="rounded-xl border-2 border-blue-200 bg-white p-3 sm:p-4">
              <h4 className="text-xs font-semibold mb-1.5 uppercase tracking-wide text-slate-500">
                Instructions
              </h4>
              <p className="text-xs text-slate-700 whitespace-pre-line leading-relaxed">
                {project.instructions.replace(
                  /IMPORTANT: Ensure you have downloaded and opened the app before starting this test\.\s*/,
                  "",
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div className="rounded-xl border-2 border-blue-400 bg-white p-3">
                <h5 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-0.5">
                  Category
                </h5>
                <p className="text-xs font-medium capitalize">{project.category}</p>
              </div>
              <div className="rounded-xl border-2 border-blue-400 bg-white p-3">
                <h5 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-0.5">
                  Test Reward
                </h5>
                <p className="text-xs font-medium text-emerald-700">
                  ${(project.reward / 100).toFixed(2)}
                </p>
                <p className="text-[10px] text-emerald-700 font-medium mt-0.5">
                  Paid instantly to your wallet upon approval
                </p>
              </div>
              <div className="rounded-xl border-2 border-blue-400 bg-white p-3">
                <h5 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-0.5">
                  Deadline
                </h5>
                <p className="text-xs font-medium">{formatDate(project.deadline)}</p>
              </div>
              {project.client && (
                <div className="rounded-xl border-2 border-blue-400 bg-white p-3">
                  <h5 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-0.5">
                    Client
                  </h5>
                  <p className="text-xs font-medium flex items-center gap-1">
                    <Building2 className="h-3.5 w-3.5 text-slate-500" />
                    {project.client}
                  </p>
                </div>
              )}
              {!project.client && (
                <div className="rounded-xl border-2 border-blue-400 bg-white p-3">
                  <h5 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-0.5">
                    Tester
                  </h5>
                  <p className="text-xs font-medium">{project.owner}</p>
                </div>
              )}
              {project.estimatedTime && (
                <div className="rounded-xl border-2 border-blue-400 bg-white p-3">
                  <h5 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-0.5">
                    Estimated Time
                  </h5>
                  <p className="text-xs font-medium">{project.estimatedTime}</p>
                </div>
              )}
            </div>

            {project.requirements && project.requirements.length > 0 && (
              <div className="mt-3 rounded-xl border-2 border-blue-200 bg-blue-50/40 p-3">
                <h5 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Requirements
                </h5>
                <div className="flex flex-wrap gap-1.5">
                  {project.requirements.map((req, idx) => (
                    <span
                      key={idx}
                      className="flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 border border-blue-100"
                    >
                      <CheckCircle2 className="h-3 w-3.5 text-blue-600" />
                      {req}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {isLocked && isAuthenticated && (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-sm">
                <p className="font-semibold text-amber-800 text-sm">Support the platform</p>
                <p className="text-xs text-amber-700 mt-1">
                  We’re a small team working to keep PataTasks running. A one-time $2 support fee
                  helps us cover server costs and development. No subscriptions, no hidden charges.
                </p>
              </div>
            )}

            <div className="flex justify-center pt-1">
              <Button
                className="w-full sm:w-auto h-10 text-sm"
                onClick={handleStartTestClick}
                disabled={completed && !isLocked}
              >
                {!isAuthenticated
                  ? "Signup to Start Test"
                  : completed
                    ? "Completed"
                    : isLocked
                       ? "Support with $2"
                      : "Start Test"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <TestApplyDialog
        project={project}
        open={showMaintenanceDialog}
        onClose={() => setShowMaintenanceDialog(false)}
        onSuccess={() => {
          setShowMaintenanceDialog(false);
          onStartTest();
        }}
        lockThresholdUsd={lockThresholdUsd}
      />
    </>
  );
}
